import React, { useState, useEffect } from 'react';
import { Loader2, Copy, Check, ExternalLink, QrCode } from 'lucide-react';
import axios from 'axios';

const API_URL = 'https://plexo-natural-api.onrender.com';

const PaymentPix = ({ amount, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [pixData, setPixData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [status, setStatus] = useState('idle'); // idle | loading | show | success | error

    const generatePix = async () => {
        setLoading(true);
        setStatus('loading');
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/api/payment/create-pix`,
                { amount },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setPixData(response.data);
                setStatus('show');
                // Start polling for status? 
                // For MVP, we use the webhook on backend, frontend can poll or wait for user to click "I paid"
            }
        } catch (err) {
            console.error('PIX Error:', err);
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(pixData.qr_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    useEffect(() => {
        generatePix();
    }, []);

    return (
        <div className="space-y-6">
            {status === 'loading' && (
                <div className="py-12 flex flex-col items-center gap-4">
                    <Loader2 className="w-12 h-12 text-green-500 animate-spin" />
                    <p className="text-white font-medium">Gerando QR Code PIX...</p>
                </div>
            )}

            {status === 'show' && pixData && (
                <div className="flex flex-col items-center gap-6 animate-fade-in">
                    {/* QR Code Frame */}
                    <div className="p-3 bg-white rounded-2xl shadow-xl shadow-green-500/10">
                        <img
                            src={`data:image/png;base64,${pixData.qr_code_base64}`}
                            alt="PIX QR Code"
                            className="w-48 h-48 rounded-lg"
                        />
                    </div>

                    <div className="w-full space-y-3">
                        <p className="text-xs text-gray-400 text-center uppercase tracking-widest">Ou copie o código</p>
                        <div className="flex bg-black/40 border border-white/10 rounded-xl overflow-hidden p-1">
                            <input
                                readOnly
                                value={pixData.qr_code}
                                className="bg-transparent border-none text-[10px] text-gray-300 px-3 flex-1 font-mono focus:ring-0"
                            />
                            <button
                                onClick={copyToClipboard}
                                className={`p-2 rounded-lg transition-colors ${copied ? 'bg-green-500 text-white' : 'bg-white/10 text-gray-400 hover:text-white'}`}
                            >
                                {copied ? <Check size={18} /> : <Copy size={18} />}
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 w-full pt-4">
                        <button
                            onClick={() => setStatus('success')}
                            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-all shadow-lg shadow-green-600/20"
                        >
                            Já realizei o pagamento
                        </button>
                        <p className="text-[10px] text-gray-500 text-center">
                            A liberação é automática após a confirmação.
                        </p>
                    </div>
                </div>
            )}

            {status === 'success' && (
                <div className="py-8 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="text-white" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-white">Processando...</h3>
                    <p className="text-sm text-gray-400">Verificando seu pagamento no sistema.</p>
                    <button onClick={onSuccess} className="mt-4 text-green-400 underline text-sm">
                        Voltar para o laudo
                    </button>
                </div>
            )}

            {status === 'error' && (
                <div className="text-center py-8 space-y-4">
                    <p className="text-red-400">Erro ao gerar PIX. Tente novamente.</p>
                    <button onClick={generatePix} className="px-6 py-2 bg-white/10 text-white rounded-lg">
                        Repetir
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentPix;
